import { differenceInCalendarDays, isValid as isValidDate, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import type { ProjectStatus } from "@/api/projects";
import { Badge } from "@/components/ui/badge";
import { PROJECT_STATUS_TONE } from "@/lib/badgeTones";

interface ProjectStatusBadgeProps {
  status: ProjectStatus;
  /** Derived overlay: shown as a caution badge beside the status, never replacing it. */
  atRisk?: boolean;
  /** Derived overlay: shown as a solid danger badge beside the status. */
  overdue?: boolean;
  /** Planned end (ISO) — feeds the overdue day count when `overdue` is true. */
  plannedEnd?: string | null;
}

export function ProjectStatusBadge({
  status,
  atRisk = false,
  overdue = false,
  plannedEnd = null,
}: ProjectStatusBadgeProps): React.ReactElement {
  const { t } = useTranslation();
  const spec = PROJECT_STATUS_TONE[status];
  const overdueDays = overdue ? overdueCalendarDays(plannedEnd) : null;
  return (
    <>
      <Badge tone={spec.tone} emphasis={spec.emphasis} data-testid={`project-status-${status}`}>
        {t(`projects.statusValues.${status}`)}
      </Badge>
      {overdue && (
        <Badge tone="danger" emphasis="solid" data-testid="project-status-overdue">
          {t("projects.overdueBadge", { count: overdueDays ?? 0 })}
        </Badge>
      )}
      {atRisk && (
        <Badge tone="caution" data-testid="project-status-at_risk">
          {t("projects.atRisk")}
        </Badge>
      )}
    </>
  );
}

function overdueCalendarDays(end: string | null): number | null {
  if (!end) return null;
  const e = parseISO(end);
  if (!isValidDate(e)) return null;
  const days = differenceInCalendarDays(new Date(), e);
  return days > 0 ? days : null;
}
